<template>
  <Content class="col-span-full" title="REALTIME_temperature">
    <div class="flex justify-end items-center mb-4">
      <div class="flex items-center gap-3" v-if="lastUpdated">
        <el-button
          type="text"
          icon="el-icon-refresh"
          @click="fetchTemperatures"
          :loading="loading"
          class="p-0 text-blue-700"
          style="font-size: 16px"
        ></el-button>
        <span class="text-sm text-gray-600">
          {{ $t('LAST_UPDATED') }}: {{ formatTime(lastUpdated) }}
        </span>
      </div>
    </div>

    <!-- Temperature Chart Section -->
    <div v-if="selectedVehicleData" class="mb-8">
      <div class="bg-white">
        <!-- Chart Header with Vehicle Info -->
        <div class="flex justify-between items-center mb-6">
          <h3 class="text-xl font-medium" v-html="chartTitle"></h3>
          <el-button
            type="text"
            icon="el-icon-close"
            @click="closeChart"
            class="text-gray-500 hover:text-gray-700"
          ></el-button>
        </div>

        <div v-if="chartLoading" class="text-center py-8">
          <i class="el-icon-loading text-2xl"></i>
          <p class="mt-2">{{ $t('LOADING') }}...</p>
        </div>

        <div v-else-if="chartError" class="text-center py-8 text-red-500">
          <i class="el-icon-warning text-2xl"></i>
          <p class="mt-2">{{ chartError }}</p>
        </div>

        <div v-else>
          <div class="mb-4">
            <VueApexCharts
              v-if="chartData.length > 0"
              type="area"
              height="350"
              :options="chartOptions"
              :series="chartData"
            />
            <div v-else class="text-center py-8 text-gray-500">
              {{ $t('NO_DATA') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <el-table
      :empty-text="$t('NO_DATA')"
      :data="temperatures"
      :stripe="true"
      v-if="!error"
      style="width: 100%"
      class="temperature-table"
      @row-click="onRowClick"
    >
      <el-table-column
        sortable
        type="index"
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        :label="$t('INDEX')"
        width="60"
      >
        <template slot-scope="scope">
          <div :data-title="$t('INDEX')">
            {{ scope.$index + 1 }}
          </div>
        </template>
      </el-table-column>

      <el-table-column
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        :label="$t('VEHICLE_TITLE')"
        width="150"
      >
        <template slot-scope="scope">
          <LicensePlate
            :license_plate="scope.row.license_plate"
            :chars="alphaList"
            class="cursor-pointer"
          />
        </template>
      </el-table-column>

      <el-table-column
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        :label="$t('DRIVER_TITLE')"
        width="180"
      >
        <template slot-scope="scope">
          <div :data-title="$t('DRIVER_TITLE')" class="cursor-pointer">
            {{ getDriverName(scope.row.driver) }}
          </div>
        </template>
      </el-table-column>

      <el-table-column
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        :label="$t('MOBILE')"
        width="140"
      >
        <template slot-scope="scope">
          <div :data-title="$t('MOBILE')" class="text-gray-600 cursor-pointer">
            {{ (scope.row.driver && scope.row.driver.mobile) || '-' }}
          </div>
        </template>
      </el-table-column>

      <el-table-column
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        :label="$t('temperature')"
        width="100"
      >
        <template slot-scope="scope">
          <div :data-title="$t('temperature')" class="cursor-pointer">
            <span
              class="flex items-center gap-2 ltr justify-end"
              v-if="scope.row.temperature"
            >
              {{ scope.row.temperature }}°C
            </span>
          </div>
        </template>
      </el-table-column>

      <el-table-column
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        :label="$t('TIME')"
        width="200"
      >
        <template slot-scope="scope">
          <div
            :data-title="$t('TIME')"
            class="text-sm text-gray-600 cursor-pointer"
            v-html="formatTimestamp(scope.row.timestamp)"
          ></div>
        </template>
      </el-table-column>
    </el-table>

    <div v-if="error && !loading">
      <div class="flex items-center justify-center" style="min-height: 100px">
        <div class="text-lg mb-2">
          {{ $t('temperature_error') }}
        </div>
      </div>
    </div>
  </Content>
</template>

<script>
import { temperatureTracking, vehicleTemperatureTracking } from '@/api/reports'
import { licenseChars } from '@/api/global'
import LicensePlate from '@/components/cars/LicensePlate'
import VueApexCharts from 'vue-apexcharts'
import { License } from '@/mixins/License'

export default {
  mixins: [License],
  components: {
    LicensePlate,
    VueApexCharts,
  },
  data() {
    return {
      temperatures: [],
      loading: false,
      error: null,
      isConnected: false,
      lastUpdated: null,
      intervalId: null,
      chartIntervalId: null,

      // Chart related data
      chartLoading: false,
      chartError: null,
      selectedVehicleData: null,
      chartData: [],
      chartTitle: '',
      selectedVehicleId: null,

      alphaList: [
        {
          code: '',
          char: '',
          id: '',
        },
      ],

      chartOptions: {
        chart: {
          id: 'temperature-chart',
          height: 350,
          type: 'area',
          stacked: false,
          toolbar: {
            show: true,
            autoSelected: 'zoom',
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true,
            },
          },
          zoom: {
            type: 'x',
            enabled: true,
            autoScaleYaxis: true,
          },
          selection: {
            enabled: true,
            type: 'x',
          },
          fontFamily: 'Vazir',
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800,
          },
        },
        dataLabels: {
          enabled: false,
        },
        markers: {
          size: 0,
          style: 'hollow',
        },
        stroke: {
          curve: 'smooth',
          width: 2,
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            inverseColors: false,
            opacityFrom: 0.5,
            opacityTo: 0.1,
            stops: [0, 90, 100],
          },
        },
        xaxis: {
          type: 'datetime',
          tickAmount: 6,
          labels: {
            formatter: function (value, timestamp) {
              return (
                new Date(timestamp).toLocaleDateString('fa-IR') +
                ' ' +
                new Date(timestamp).toLocaleTimeString('fa-IR', {
                  hour: '2-digit',
                  minute: '2-digit',
                })
              )
            },
            style: {
              fontSize: '12px',
            },
            rotate: -45,
            rotateAlways: false,
          },
          title: {
            text: 'زمان',
            style: {
              fontSize: '14px',
              fontWeight: 600,
            },
          },
        },
        yaxis: {
          title: {
            text: 'دما (°C)',
            style: {
              fontSize: '14px',
              fontWeight: 600,
            },
          },
          labels: {
            formatter: function (value) {
              return value?.toFixed(1) + '°C'
            },
          },
        },
        tooltip: {
          shared: false,
          y: {
            formatter: function (value) {
              return value + '°C'
            },
          },
          x: {
            formatter: function (value) {
              return new Date(value).toLocaleString('fa-IR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })
            },
          },
        },
        grid: {
          borderColor: '#e7e7e7',
          row: {
            colors: ['#f3f3f3', 'transparent'],
            opacity: 0.5,
          },
        },
        colors: ['#008FFB'],
        legend: {
          show: true,
          position: 'top',
          horizontalAlign: 'left',
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                height: 250,
              },
              xaxis: {
                labels: {
                  rotate: -90,
                },
              },
            },
          },
        ],
      },
    }
  },

  mounted() {
    this.loadAlphaList()
    this.startTracking()
  },

  beforeDestroy() {
    this.stopTracking()
    this.stopChartTracking()
  },

  methods: {
    async loadAlphaList() {
      try {
        const res = await licenseChars.api()
        if (res) {
          this.alphaList = res
        }
      } catch (error) {
        console.error('Error loading license chars:', error)
      }
    },

    async startTracking() {
      await this.fetchTemperatures()

      this.intervalId = setInterval(() => {
        this.fetchTemperatures()
      }, 60000)
    },

    stopTracking() {
      if (this.intervalId) {
        clearInterval(this.intervalId)
        this.intervalId = null
      }
    },

    async fetchTemperatures() {
      try {
        this.loading = true
        this.error = null

        const response = await temperatureTracking.api()

        if (response && Array.isArray(response)) {
          this.temperatures = response.map((item) => ({
            ...item,
            refreshing: false,
          }))
          this.isConnected = true
          this.lastUpdated = new Date()

          // If a vehicle chart is currently displayed, refresh its data too
          if (this.selectedVehicleData && this.selectedVehicleId) {
            await this.fetchVehicleTemperatureData(
              this.selectedVehicleId,
              false
            )
          }
        } else {
          this.error = 'Invalid response format'
          this.isConnected = false
        }
      } catch (error) {
        console.error('Error fetching temperature data:', error)
        this.error = 'Failed to fetch temperature data'
        this.isConnected = false
      } finally {
        this.loading = false
      }
    },

    async fetchVehicleTemperatureData(vehicleId, showLoading = true) {
      try {
        if (showLoading) {
          this.chartLoading = true
        }
        this.chartError = null

        const today = new Date()
        today.setHours(0, 0, 0, 0) // Set to 12AM today
        const startRange = this.convertToTimestamp(today)

        const stopTime = this.lastUpdated || new Date()
        stopTime.setSeconds(0, 0)
        const stopRange = this.convertToTimestamp(stopTime)

        const response = await vehicleTemperatureTracking.api(
          vehicleId,
          startRange,
          stopRange
        )

        if (response) {
          this.selectedVehicleData = response

          // Process temperature data for chart from the API structure
          if (
            response.metric &&
            response.metric.data &&
            Array.isArray(response.metric.data)
          ) {
            const chartData = response.metric.data.map((item) => ({
              x: new Date(item.datetime).getTime(),
              y: parseFloat(item.value),
            }))

            // Sort data by timestamp to ensure proper chart display
            chartData.sort((a, b) => a.x - b.x)

            this.chartData = [
              {
                name: this.$t('temperature'),
                data: chartData,
              },
            ]
          } else if (
            response.temperature_data &&
            Array.isArray(response.temperature_data)
          ) {
            // Fallback for old API structure
            const chartData = response.temperature_data.map((item) => ({
              x: new Date(item.timestamp).getTime(),
              y: parseFloat(item.temperature),
            }))

            chartData.sort((a, b) => a.x - b.x)

            this.chartData = [
              {
                name: this.$t('temperature'),
                data: chartData,
              },
            ]
          } else {
            // If no temperature data, show error instead of mock data
            console.log('No temperature data found in response')
            this.$notify.error({
              title: this.$i18n.t('NO_DATA'),
              duration: 5000,
              customClass: 'error_msg',
            })
            this.closeChart()
          }
        } else {
          this.$notify.error({
            title: this.$i18n.t('NO_DATA'),
            duration: 5000,
            customClass: 'error_msg',
          })
          this.closeChart()
        }
      } catch (error) {
        console.error('Error fetching vehicle temperature data:', error)
        this.$notify.error({
          title: this.$i18n.t('NO_DATA'),
          duration: 5000,
          customClass: 'error_msg',
        })
        this.closeChart()
      } finally {
        if (showLoading) {
          this.chartLoading = false
        }
      }
    },

    closeChart() {
      this.selectedVehicleData = null
      this.chartData = []
      this.chartError = null
    },

    getDriverName(driver) {
      if (!driver) return '-'
      return (
        `${driver.first_name || ''} ${driver.last_name || ''}`.trim() || '-'
      )
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return '-'

      return `<div class="flex items-center space-x-1 justify-end flex-row-reverse">${new Date(
        timestamp
      ).toLocaleDateString(
        'fa-IR'
      )}<span class="text-gray-500 symbol ml-1">|</span><span>${new Date(
        timestamp
      ).toLocaleTimeString('fa-IR')}</span></div>`
    },

    formatTime(date) {
      return date.toLocaleTimeString('fa-IR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    },

    onRowClick(row) {
      // Set the selected vehicle data
      this.selectedVehicleId = row.id || row.vehicle_id
      this.chartTitle = `${this.$t('temperature')}  ${
        this.License(row.license_plate, this.alphaList) || 'نامشخص'
      }`

      // Fetch temperature data for the selected vehicle
      this.fetchVehicleTemperatureData(this.selectedVehicleId)
    },

    convertToTimestamp(datetimeString) {
      return new Date(datetimeString).getTime() / 1000
    },

    stopChartTracking() {
      if (this.chartIntervalId) {
        clearInterval(this.chartIntervalId)
        this.chartIntervalId = null
      }
    },
  },
}
</script>

<style>
.temperature-table .el-table__row {
  cursor: pointer;
}

.temperature-table .el-table__row:hover {
  background-color: #f5f7fa;
}

/* Chart section styles */
.temperature-chart-section {
  transition: all 0.3s ease;
}

.temperature-chart-section .chart-header {
  border-bottom: 1px solid #e0e6ed;
  padding-bottom: 1rem;
  margin-bottom: 1.5rem;
}
</style>
